import React from 'react';
import { Form, FormProps } from '@doveit/bricks';
import { NonLegalSellerAmlValidationSchema } from './schema';
import { BooleanString } from '../../../../../utils/boolean-string/booleanString';

export interface NonLegalSellerAmlFormProps {
  initialValues?: Partial<NonLegalSellerAmlFormModel>,
  loading?: FormProps<NonLegalSellerAmlFormModel>['loading'],
  disabled?: FormProps<NonLegalSellerAmlFormModel>['disabled'],
  formRef?: FormProps<NonLegalSellerAmlFormModel>['innerRef'],
  onSubmit: FormProps<NonLegalSellerAmlFormModel>['onSubmit']
}

export interface NonLegalSellerAmlFormModel {
  politicallyExposed: BooleanString,
  relatedToAPoliticallyExposedFigure: BooleanString,
  thirdPartyIncomeGoodsSource: boolean,
  successionOrDonationGoodsSource: boolean,
  sellingPersonalPropertiesGoodsSource: boolean,
  otherGoodsSource: boolean,
  otherGoodsSourceValue?: string,
  notes?: string,
}

const DEFAULT_INITIAL_VALUES: NonLegalSellerAmlFormModel = {
  politicallyExposed: '',
  relatedToAPoliticallyExposedFigure: '',
  sellingPersonalPropertiesGoodsSource: false,
  thirdPartyIncomeGoodsSource: false,
  successionOrDonationGoodsSource: false,
  otherGoodsSource: false,
  otherGoodsSourceValue: '',
  notes: '',
};

const NonLegalSellerAmlForm: React.FC<NonLegalSellerAmlFormProps> = ({
  initialValues,
  disabled,
  formRef,
  onSubmit,
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...DEFAULT_INITIAL_VALUES,
    ...initialValues,
  }), [initialValues]);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      disabled={disabled}
      validationSchema={NonLegalSellerAmlValidationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue }) => {
        const onOtherGoodsSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!e.target.checked) {
            setFieldValue('otherGoodsSourceValue', '');
          }
        };

        return (
          <>
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                aria-label="Campo per indicare se la persona è politicamente esposta"
                label="Persona politicamente esposta"
                name="politicallyExposed"
              />
            </Form.Item>
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                aria-label="Campo per indicare se parente o convivente di persona politicamente esposta"
                label="Parente o convivente di persona politicamente esposta"
                name="relatedToAPoliticallyExposedFigure"
              />
            </Form.Item>

            <Form.Fieldset legend="Provenienza del bene">

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.CheckboxButton
                  aria-label="Campo per indicare se il bene è stato acquistato o costruito con redditi propri"
                  name="sellingPersonalPropertiesGoodsSource"
                  text="Acquistato/costruito con redditi propri"
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.CheckboxButton
                  aria-label="Campo per indicare se il bene è stato acquistato o costruito con redditi di terzi"
                  name="thirdPartyIncomeGoodsSource"
                  text="Acquistato/costruito con redditi di terzi"
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.CheckboxButton
                  aria-label="Campo per indicare se il bene è derivante da successione o donazione"
                  name="successionOrDonationGoodsSource"
                  text="Successione/donazione"
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.CheckboxButton
                  aria-label="Campo per indicare se il bene è proveniente da altre sorgenti"
                  name="otherGoodsSource"
                  text="Altro"
                  onChange={onOtherGoodsSourceChange}
                />
              </Form.Item>
              <Form.Item>
                <Form.Input
                  aria-label="Campo per indicare altre sorgenti del bene"
                  name="otherGoodsSourceValue"
                  placeholder="Specifica la provenienza"
                  disabled={!values.otherGoodsSource}
                />
              </Form.Item>
            </Form.Fieldset>
            <Form.Item>
              <Form.TextArea
                name="notes"
                label="Osservazioni e note"
                rows={5}
              />
            </Form.Item>
          </>
        );
      }}
    </Form>
  );
};

export default NonLegalSellerAmlForm;
